<template>
    <div class="image-model-page relative xl:pt-[6rem] pt-[3rem]">
        <img
            class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] object-cover z-0 pointer-events-none"
            :src="heroMaterial.img"
            :alt="heroMaterial.alt || pageConfig.generator.title"
            fetchpriority="high"
            decoding="async"
        />
        <div
            class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] pointer-events-none z-[1]"
        ></div>

        <section
            ref="generatorSection"
            class="video-generator-section relative z-11"
        >
            <div class="container">
                <div class="generator-container">
                    <div class="generator-header">
                        <div class="generator-title">
                            <div class="page-name animate-float backdrop-blur-md">
                                <span class="light-dot"></span>
                                <span>{{
                                    pageConfig.generator.modelOnlineLabel
                                }}</span>
                            </div>
                            <h1 v-if="!showComingSoonNotice">
                                {{ pageConfig.generator.title }}
                            </h1>
                        </div>
                    </div>

                    <div
                        v-if="showComingSoonNotice"
                        class="mx-auto mt-8 flex min-h-[220px] w-full max-w-[1040px] items-center justify-center px-6 py-12 text-center md:min-h-[320px]"
                    >
                        <p
                            class="!text-[70px] font-semibold tracking-[0.2em] text-white md:text-[56px]"
                        >
                            {{ comingSoonNoticeText }}
                        </p>
                    </div>

                    <div v-else class="hidden md:block">
                        <NewImageGenerator
                            v-if="usesRedirectGenerator"
                            :model-name="pageConfig.generator.modelName"
                            :model-key="pageConfig.generator.modelKey"
                            :default-tab="pageConfig.generator.defaultTab"
                            :require-prompt-for-image="
                                pageConfig.generator.requirePromptForImage
                            "
                            :image-prompt-placeholder="
                                pageConfig.generator.imagePromptPlaceholder
                            "
                        />
                        <ImageWorkbenchGenerator
                            v-else
                            :model-key="pageConfig.generator.modelKey"
                            :default-tab="pageConfig.generator.defaultTab"
                        />
                    </div>

                    <div v-if="!showComingSoonNotice" class="block md:hidden">
                        <MobileGenerator
                            type="image"
                            :model-name="pageConfig.generator.modelName"
                            :model-key="pageConfig.generator.modelKey"
                            :default-tab="pageConfig.generator.defaultTab"
                            :require-prompt-for-image="
                                pageConfig.generator.requirePromptForImage
                            "
                            :image-prompt-placeholder="
                                pageConfig.generator.imagePromptPlaceholder
                            "
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- 图片模型暂时不支持社区精选（正式环境没数据） -->
        <CommunityWorks
            :model-key="pageConfig.generator.modelKey"
            filter-type="IMG_TO_IMG"
            v-if="false"
        />

        <section
            :class="[
                'core-features-section relative z-10 !bg-transparent',
                {
                    'core-features-section--no-padding':
                        isCoreFeaturesPaddingDisabled,
                },
            ]"
        >
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title mt-[2rem]">
                        {{ pageConfig.coreFeatures.title }}
                    </h2>
                    <p
                        v-if="pageConfig.coreFeatures.subtitle"
                        class="text-white/60 mt-4 text-center text-lg"
                    >
                        {{ pageConfig.coreFeatures.subtitle }}
                    </p>
                </div>
                <div
                    class="features-showcase-container grid grid-cols-1 xl:grid-cols-2 gap-[80px]"
                >
                    <div
                        v-for="feature in pageConfig.coreFeatures.items"
                        :key="feature.title"
                        class="feature-card"
                    >
                        <div class="flex flex-col">
                            <div class="feature-video">
                                <img
                                    class="rounded-[20px]"
                                    :src="feature.url"
                                    :alt="feature.title"
                                />
                            </div>
                            <div class="feature-text !px-0">
                                <h3 class="feature-title">
                                    {{ feature.title }}
                                </h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ pageConfig.howToUse.title }}
                    </h2>
                    <h3
                        v-if="pageConfig.howToUse.subtitle"
                        class="text-text-adad text-base"
                    >
                        {{ pageConfig.howToUse.subtitle }}
                    </h3>
                </div>
                <ol
                    class="gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4"
                >
                    <li
                        v-for="(step, index) in pageConfig.howToUse.steps"
                        :key="index"
                        class="step-card flex flex-col items-center justify-center gap-5 min-h-[150px] md:min-h-[200px] lg:min-h-[270px] border border-white/10 rounded-[20px] p-5"
                    >
                        <img
                            :src="step.icon"
                            :alt="step.title"
                            class="step-icon w-[96px] h-auto"
                        />
                        <div class="step-content">
                            <h3 class="text-[20px] font-bold text-white">
                                {{ step.title }}
                            </h3>
                            <p
                                v-html="step.description"
                                class="text-sm text-text-adad"
                            ></p>
                        </div>
                    </li>
                </ol>
            </div>
            <p
                class="generator-description !mt-6 !mx-auto !max-w-5xl px-[1rem] !text-text-adad"
                v-html="pageConfig.generator.description"
            ></p>
        </section>

        <YouTubeCarouselSection
            v-if="pageConfig.media.youtubeVideos.length > 0"
            :title="pageConfig.media.youtubeTitle"
            :videos="pageConfig.media.youtubeVideos"
        />

        <TwitterPosts
            v-if="pageConfig.media.twitterTweetUrls.length > 0"
            :title="pageConfig.media.xPostsTitle"
            :tweet-urls="pageConfig.media.twitterTweetUrls"
        />

        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ pageConfig.exploreModels.title }}
                    </h2>
                    <h3
                        v-if="pageConfig.exploreModels.subtitle"
                        class="text-text-adad text-base mt-2"
                    >
                        {{ pageConfig.exploreModels.subtitle }}
                    </h3>
                </div>

                <div class="models-grid">
                    <NuxtLink
                        v-for="model in pageConfig.exploreModels.items"
                        :key="`${model.to}-${model.title}`"
                        :to="model.to"
                        class="model-card !bg-[#22242D]"
                    >
                        <div class="model-icon">
                            <img
                                :src="model.icon"
                                :alt="model.alt"
                                loading="lazy"
                            />
                        </div>
                        <h3 class="model-card-title">{{ model.title }}</h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ pageConfig.faq.title }}</h2>
                </div>

                <div class="faq-list">
                    <div
                        v-for="(faq, index) in faqData"
                        :key="index"
                        class="faq-item"
                        :class="{ active: activeFaqIndex === index }"
                    >
                        <div class="faq-question" @click="toggleFaq(index)">
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ModelPageDisclaimerSection :text="pageConfig.pageDisclaimer" />

        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img
                                :src="pageConfig.cta.logo"
                                :alt="pageConfig.cta.logoAlt"
                                class="logo-image"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    <div class="cta-header">
                        <h2 class="cta-title">{{ pageConfig.cta.title }}</h2>
                        <p class="cta-description">
                            {{ pageConfig.cta.description }}
                        </p>
                    </div>

                    <div class="cta-actions">
                        <button
                            @click="scrollToGenerator"
                            class="cta-primary-btn"
                        >
                            {{ pageConfig.cta.buttonText }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ pageConfig.cta.disclaimer }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useHead } from '#imports'
import { analyticsEvent } from '~/utils/log-event-func'
import { useFaqSeo } from '~/composables/useFaqSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useSettingImageSEO } from '~/composables/useSettingImageSEO'
import { useVideoSeo } from '~/composables/useVideoSeo'
import CommunityWorks from '~/components/CommunityWorks.vue'
import ImageWorkbenchGenerator from '~/components/model/image/ImageWorkbenchGenerator.vue'
import NewImageGenerator from '~/components/NewImageGenerator.vue'
import MobileGenerator from '~/components/mobile/MobileGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import type {
    ImageHeroMaterial,
    ImageLandingConfig,
} from '~/utils/landingPageConfig'

const props = defineProps<{
    pageConfig: ImageLandingConfig
}>()

const { locale } = useI18n()
const generatorSection = ref<HTMLElement | null>(null)
const activeFaqIndex = ref<number | null>(null)

const heroMaterial = computed<ImageHeroMaterial>(() => {
    const localeMaterial = props.pageConfig.hero.byLocale?.[locale.value]
    return {
        ...props.pageConfig.hero.default,
        ...localeMaterial,
    }
})

const faqData = computed(() => props.pageConfig.faq.items)
const howToUseSteps = computed(() =>
    props.pageConfig.howToUse.steps.map(step => ({
        title: step.title,
        description: step.description,
    }))
)
const showComingSoonNotice = computed(
    () => props.pageConfig.generator.showComingSoonNotice === false
)
const comingSoonNoticeText = computed(
    () => props.pageConfig.generator.comingSoonNotice || 'Coming Soon'
)
const isCoreFeaturesPaddingDisabled = computed(
    () => props.pageConfig.generator.modelKey === 'GPT_IMAGE_2'
)
// GPT Image 2 must use the established model-page redirect handoff. Keep the
// existing embedded workbench for other dynamic pages until they are migrated
// and verified independently.
const usesRedirectGenerator = computed(
    () => props.pageConfig.generator.modelKey === 'GPT_IMAGE_2'
)

const { canonicalUrl } = useMultilingualSeo({
    title: props.pageConfig.seo.title,
    description: props.pageConfig.seo.description,
    keywords: props.pageConfig.seo.keywords,
    ogTitle: props.pageConfig.seo.ogTitle,
    ogDescription: props.pageConfig.seo.ogDescription,
    ogImage: props.pageConfig.seo.ogImage,
    ogImageAlt:
        props.pageConfig.seo.ogImageAlt || heroMaterial.value.alt || '',
    twitterTitle: props.pageConfig.seo.twitterTitle,
    twitterDescription: props.pageConfig.seo.twitterDescription,
    twitterImage:
        props.pageConfig.seo.twitterImage || props.pageConfig.seo.ogImage,
    twitterImageAlt:
        props.pageConfig.seo.twitterImageAlt ||
        props.pageConfig.seo.ogImageAlt ||
        heroMaterial.value.alt ||
        '',
})

useHowToSeo(
    computed(
        () =>
            props.pageConfig.seo.howToName || props.pageConfig.howToUse.title
    ),
    howToUseSteps
)

useFaqSeo(faqData)

useVideoSeo(
    props.pageConfig.media.youtubeVideos.map(video => ({
        title: video.title,
        embedUrl: video.embedUrl,
        mainEntityOfPage: canonicalUrl.value,
    })),
    { primaryOnly: false }
)

useSettingImageSEO({
    key: props.pageConfig.seo.key,
    title: props.pageConfig.generator.title,
    description: props.pageConfig.seo.description,
    contentUrl:
        props.pageConfig.seo.contentUrl || heroMaterial.value.img || '',
    thumbnailUrl:
        props.pageConfig.seo.thumbnailUrl ||
        props.pageConfig.seo.ogImage ||
        heroMaterial.value.img ||
        '',
    mainEntityOfPage: canonicalUrl.value,
})

useHead(() => ({
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: heroMaterial.value.img,
        },
    ],
}))

useHead(() => ({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://www.weryai.com/',
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name:
                            props.pageConfig.seo.breadcrumbName ||
                            props.pageConfig.generator.title,
                        item: canonicalUrl.value,
                    },
                ],
            }),
        },
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: props.pageConfig.generator.title,
                description: props.pageConfig.seo.description,
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: 'WeryAI',
                    url: 'https://www.weryai.com',
                    sameAs: [
                        'https://twitter.com/weryai',
                        'https://www.youtube.com/@weryai',
                        'https://www.instagram.com/weryai_official/',
                        'https://www.tiktok.com/@weryai_',
                        'https://discord.gg/wbEdbTvz',
                    ],
                },
                featureList: props.pageConfig.seo.featureList,
            }),
        },
    ],
}))

const scrollToGenerator = () => {
    generatorSection.value?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    })
}

const toggleFaq = (index: number) => {
    activeFaqIndex.value = activeFaqIndex.value === index ? null : index
}

onMounted(() => {
    analyticsEvent(props.pageConfig.analytics.pageViewEvent)
    analyticsEvent('view_model_page_common', {
        model_name: props.pageConfig.analytics.modelName,
        type: 'image',
    })
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

@media (max-width: 768px) {
    .container {
        padding: 0 16px !important;
    }

    .generator-container {
        padding: 0 !important;
        max-width: 100% !important;
    }

    .feature-card {
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        background: #120e11;
        padding: 16px;
        overflow: hidden;
    }

    .features-showcase-container {
        gap: 16px !important;
    }

    .how-to-use-section ol {
        gap: 12px !important;
    }

    .how-to-use-section ol li.step-card {
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 16px !important;
        min-height: auto !important;
        padding: 16px !important;
        border-radius: 16px !important;
    }

    .how-to-use-section ol li.step-card:nth-child(1),
    .how-to-use-section ol li.step-card:nth-child(2) {
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: rgba(255, 255, 255, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    .how-to-use-section ol li.step-card:nth-child(3) {
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: rgba(153, 247, 104, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    .how-to-use-section .step-icon {
        width: 64px !important;
        height: auto !important;
        flex-shrink: 0;
    }

    .how-to-use-section .step-content {
        flex: 1;
        text-align: left;
    }

    .how-to-use-section .step-content h3 {
        font-size: 16px !important;
        text-align: left !important;
        margin-bottom: 4px;
    }

    .how-to-use-section .step-content p {
        text-align: left !important;
        margin: 0;
    }
}

.core-features-section--no-padding {
    padding: 0 !important;
}
</style>
